import axios from 'axios';
import HttpStatus from 'http-status-codes';
import { ApolloClient, gql } from '@apollo/client';

import { getTokenPromise } from '@trqk/utils';

const GET_TRQK_AUDIO = gql`
    query getTrqkAudio($id: ID!) {
        getTrqkAudio(id: $id) {
            id
            song_id
        }
    }
`;

interface UpdateTrqkAudioWithSongIdInput {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    client: ApolloClient<any>;
    getToken: GetToken;
    sendTaskEvent: SendTaskEvent;
    taskId: string;
    trqkAudioId: string;
    trqkDirectoryUrl: string;
    trqkSongId: string;
}

interface UpdateTrqkAudioWithSongIdResult {
    trqkAudioId: string;
    trqkSongId: string;
}

/**
 * Update an existing `trqkAudio` with a new `song_id`, sending task events for either SUCCESS or
 * ERROR. Also, update the value of the trqkAudio.song_id in the Apollo Client cache.
 */
export const updateTrqkAudioWithSongId = async ({
    client,
    getToken,
    sendTaskEvent,
    taskId,
    trqkAudioId,
    trqkDirectoryUrl,
    trqkSongId,
}: UpdateTrqkAudioWithSongIdInput): Promise<
    UpdateTrqkAudioWithSongIdResult | undefined
> => {
    const token = await getTokenPromise(getToken);

    try {
        const response = await axios({
            method: 'post',
            url: trqkDirectoryUrl,
            headers: {
                Authorization: token,
            },
            // throw an error for non-200 status
            validateStatus: (status: number): boolean =>
                status === HttpStatus.OK,
            data: {
                query: `
                mutation updateTrqkAudio($input: UpdateTrqkAudioInput!) {
                    updateTrqkAudio(input: $input) {
                        id
                        song_id
                    }
                }
                `,
                variables: {
                    input: {
                        id: trqkAudioId,
                        song_id: trqkSongId,
                    },
                },
            },
        });

        if (response?.data?.errors) {
            throw new Error(
                `Error(s) in GraphQL mutation: \n${JSON.stringify(
                    response.data.errors,
                    null,
                    4,
                )}`,
            );
        }

        sendTaskEvent({
            type: 'SUCCESS',
            value: {
                taskId,
                output: {
                    trqkAudioId,
                    trqkSongId,
                },
            },
        });

        const cacheQuery = client.cache.readQuery({
            query: GET_TRQK_AUDIO,
            variables: {
                id: trqkAudioId,
            },
        });

        // eslint-disable-next-line
        // @ts-ignore
        const cachedTrqkAudio = cacheQuery?.getTrqkAudio ?? null;

        client.cache.writeQuery({
            query: GET_TRQK_AUDIO,
            variables: {
                id: trqkAudioId,
            },
            data: {
                getTrqkAudio: {
                    ...cachedTrqkAudio,
                    song_id: trqkSongId,
                },
            },
        });

        return {
            trqkAudioId,
            trqkSongId,
        };
    } catch (error) {
        sendTaskEvent({
            type: 'ERROR',
            value: {
                taskId,
                error: `Failed to update trqkAudioId: ${trqkAudioId} with trqkSongId: ${trqkSongId}`,
            },
        });

        return undefined;
    }
};
